import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ModeProvider } from "@/context/ModeContext";
import { BillingProvider } from "@/context/BillingContext";
import { safeNext } from "@/lib/api";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Dashboard from "@/pages/Dashboard";
import CoinDetail from "@/pages/CoinDetail";
import Portfolio from "@/pages/Portfolio";
import Chat from "@/pages/Chat";
import News from "@/pages/News";
import Performance from "@/pages/Performance";
import StrategyLab from "@/pages/StrategyLab";
import Alerts from "@/pages/Alerts";
import Settings from "@/pages/Settings";
import Learn, { Lesson } from "@/pages/Learn";
import Opportunities from "@/pages/Opportunities";
import LearnAdmin from "@/pages/LearnAdmin";
import Journal from "@/pages/Journal";
import Pricing from "@/pages/Pricing";
import PaymentSuccess, { PaymentCancel } from "@/pages/PaymentSuccess";
import DeleteAccount from "@/pages/DeleteAccount";
import Privacy from "@/pages/Privacy";
import Landing from "@/pages/site/Landing";
import About from "@/pages/site/About";
import Contact from "@/pages/site/Contact";
import Partners from "@/pages/site/Partners";
import Terms from "@/pages/site/Terms";
import RiskDisclosure from "@/pages/site/RiskDisclosure";
import Transparency from "@/pages/site/Transparency";
import Glossary from "@/pages/site/Glossary";
import Markets from "@/pages/site/Markets";
import FeedHealth from "@/pages/site/FeedHealth";
import Trade from "@/pages/Trade";
import Owner from "@/pages/Owner";
import Install from "@/pages/site/Install";
import AdminFeedback from "@/pages/admin/AdminFeedback";
import AdminPartners from "@/pages/admin/AdminPartners";
import { FeedbackProvider } from "@/context/FeedbackContext";
import { LiveMarketProvider } from "@/context/LiveMarketContext";

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  return children;
};

const Home = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  return user ? <Navigate to="/dashboard" replace /> : <Landing />;
};

const PricingShell = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  return user ? <Layout /> : <Outlet />;  // pricing is public; signed-in users keep the app chrome
};

const Public = ({ children }) => {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  if (user) return <Navigate to={safeNext(new URLSearchParams(loc.search).get("next"))} replace />;
  return children;
};

export default function App() {
  return (
    <AuthProvider>
      <ModeProvider>
      <BillingProvider>
      <BrowserRouter>
        <Toaster
          theme="dark" position="top-right" richColors
          toastOptions={{ style: { background: "#0F141C", border: "1px solid #1E293B", color: "#F8FAFC" } }}
        />
        <FeedbackProvider>
        <LiveMarketProvider>
        <Routes>
          <Route path="/login" element={<Public><Login /></Public>} />
          <Route path="/signup" element={<Public><Signup /></Public>} />
          <Route path="/delete-account" element={<DeleteAccount />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/partners" element={<Partners />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/risk-disclosure" element={<RiskDisclosure />} />
          <Route path="/transparency" element={<Transparency />} />
          <Route path="/glossary" element={<Glossary />} />
          <Route path="/markets" element={<Markets />} />
          <Route path="/coinbase-listed" element={<Navigate to="/markets" replace />} />
          <Route path="/feed-health" element={<FeedHealth />} />
          <Route element={<PricingShell />}>
            <Route path="/pricing" element={<Pricing />} />
          </Route>
          <Route path="/install" element={<Install />} />
          <Route path="/" element={<Home />} />
          <Route element={<Protected><Layout /></Protected>}>
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="trade" element={<Trade />} />
            <Route path="owner" element={<Owner />} />
            <Route path="coin/:coinId" element={<CoinDetail />} />
            <Route path="portfolio" element={<Portfolio />} />
            <Route path="chat" element={<Chat />} />
            <Route path="news" element={<News />} />
            <Route path="performance" element={<Performance />} />
            <Route path="track-record" element={<Navigate to="/performance?tab=signals" replace />} />
            <Route path="quality" element={<Navigate to={{ pathname: "/performance", search: "?tab=quality", hash: window.location.hash }} replace />} />
            <Route path="strategy-lab" element={<StrategyLab />} />
            <Route path="alerts" element={<Alerts />} />
            <Route path="journal" element={<Journal />} />
            <Route path="settings" element={<Settings />} />
            <Route path="opportunities" element={<Opportunities />} />
            <Route path="learn" element={<Learn />} />
            <Route path="learn/admin" element={<LearnAdmin />} />
            <Route path="learn/:slug" element={<Lesson />} />
            <Route path="admin/feedback" element={<AdminFeedback />} />
            <Route path="admin/partners" element={<AdminPartners />} />
            <Route path="payment/success" element={<PaymentSuccess />} />
            <Route path="payment/cancel" element={<PaymentCancel />} />
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        </LiveMarketProvider>
        </FeedbackProvider>
      </BrowserRouter>
      </BillingProvider>
      </ModeProvider>
    </AuthProvider>
  );
}
